import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CreateCompanyModel } from '@/app/models/createCompanyModel'
import { createCompany, getRoles } from '@/lib/api/company/createCompany'
import { RolePermissionModel } from '@/app/models/rolePermissionModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export interface CompanyFormData {
  companyName: string
  invoiceCompanyName: string
  uniqueIdentifier: string
  nip: string
  regon: string
  krs: string
  email: string
  notificationsEmail: string
  contactEmail: string
  contactPhone: string
  licenseNumber: string
  street: string
  houseNumber: string
  buildingNumber: string
  postalCode: string
  city: string
}

const initialFormData: CompanyFormData = {
  companyName: '',
  invoiceCompanyName: '',
  uniqueIdentifier: '',
  nip: '',
  regon: '',
  krs: '',
  email: '',
  notificationsEmail: '',
  contactEmail: '',
  contactPhone: '',
  licenseNumber: '',
  street: '',
  houseNumber: '',
  buildingNumber: '',
  postalCode: '',
  city: '',
}

export function useCompanyForm(companyId: string) {
  const { t } = useLocalize()
  const [formData, setFormData] = React.useState<CompanyFormData>(initialFormData)
  const [roles, setRoles] = React.useState<RolePermissionModel[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)
  const queryClient = useQueryClient()

  // Load initial data
  React.useEffect(() => {
    const loadData = async () => {
      try {
        const rolesRes = await getRoles()
        setRoles(rolesRes.data)

        // Role assignment for creating user is handled server-side
      } catch (err) {
        console.error('Error loading form data:', err)
      }
    }
    loadData()
  }, [companyId])

  const updateField = <K extends keyof CompanyFormData>(
    field: K,
    value: CompanyFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError(null)
  }

  const submit = async (): Promise<{ success: boolean; companyId?: string }> => {
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const companyToCreate: CreateCompanyModel = {
        companyName: formData.companyName,
        invoiceCompanyName: formData.invoiceCompanyName || undefined,
        uniqueIdentifier: formData.uniqueIdentifier,
        nip: formData.nip,
        regon: formData.regon,
        krs: formData.krs,
        email: formData.email,
        notificationsEmail: formData.notificationsEmail,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
        licenseNumber: formData.licenseNumber || undefined,
        street: formData.street || undefined,
        houseNumber: formData.houseNumber || undefined,
        buildingNumber: formData.buildingNumber || undefined,
        postalCode: formData.postalCode || undefined,
        city: formData.city || undefined,
        sourceCompanyId: companyId,
      }

      const response = await createCompany(companyToCreate)
      setSuccess(true)
      setFormData(initialFormData)
      await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
      return { success: true, companyId: response.data.id }
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || t('addCompany.create-error'),
      )
      return { success: false }
    } finally {
      setLoading(false)
    }
  }

  return {
    formData,
    updateField,
    submit,
    loading,
    error,
    success,
    roles,
  }
}
